import React, { useState, useEffect, useContext } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, CircularProgress, Alert, InputAdornment, Tooltip, Rating, FormControlLabel, Checkbox } from '@mui/material';
import { Add, Search, Edit, Delete, CheckCircle, Cancel, Feedback, Download, Star, AssignmentTurnedIn, Assignment, History, Timeline, PersonAdd } from '@mui/icons-material';
import { NavigateBefore as PrevIcon, NavigateNext as NextIcon } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { toLocalInputValue } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

const API = '/api';
const PS = 15;
const SC = {scheduled:'#2196F3',confirmed:'#9C27B0',completed:'#4CAF50',cancelled:'#F44336','no-show':'#FF9800',converted:'#1B7A3E'};
const EF = {title:'',lead:'',assignedTo:'',startTime:'',endTime:'',status:'scheduled',notes:'',location:'',requirement:'',message:'',appointmentStatus:'pending'};
const FF = {customerName:'',customerPhone:'',customerEmail:'',overallRating:0,serviceRating:0,staffBehavior:0,productKnowledge:0,wouldRecommend:false,interestedInSolar:false,comments:'',followUpRequired:false};

export default function Appointments() {
  const {user} = useContext(AuthContext);
  const navigate = useNavigate();
  const [appts,setAppts] = useState([]);
  const [users,setUsers] = useState([]);
  const [leads,setLeads] = useState([]);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [success,setSuccess] = useState('');
  const [page,setPage] = useState(1);
  const [search,setSearch] = useState('');
  const [sf,setSf] = useState('');
  const [open,setOpen] = useState(false);
  const [editA,setEditA] = useState(null);
  const [form,setForm] = useState(EF);
  const [saving,setSaving] = useState(false);
  const [fe,setFe] = useState('');
  const [fbOpen,setFbOpen] = useState(false);
  const [fbAppt,setFbAppt] = useState(null);
  const [fbForm,setFbForm] = useState(FF);
  const [fbSaving,setFbSaving] = useState(false);
  const [fbError,setFbError] = useState('');
  const [existingFeedback,setExistingFeedback] = useState(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpAppt, setFollowUpAppt] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({remarks:'',outcome:'',nextFollowUp:''});
  const [followUpSaving, setFollowUpSaving] = useState(false);
  const [followUpError, setFollowUpError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAppt, setHistoryAppt] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAppt, setAssignAppt] = useState(null);
  const [assignSP, setAssignSP] = useState('');
  const [visitAppt, setVisitAppt] = useState(null);
  const [visitForm, setVisitForm] = useState({visitCompleted:true,visitOutcome:'',visitRemarks:'',visitDate:''});
  const [visitSaving, setVisitSaving] = useState(false);
  const [visitError, setVisitError] = useState('');

  const hdr = {headers:{Authorization:'Bearer '+localStorage.getItem('token')}};

  const load = async () => {
    try {
      setLoading(true);
      const [ar,lr,ur] = await Promise.all([
        axios.get(API+'/appointments',hdr),
        axios.get(API+'/leads?limit=2000&page=1',hdr),
        axios.get(API+'/users/team',hdr),
      ]);
      setAppts(ar.data.appointments||[]);
      setLeads(lr.data.leads||[]);
      setUsers(ur.data.users||[]);
    } catch(e) { setError(e.response?.data?.message||'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);

  const filtered = appts.filter(a=>{
    const q=search.toLowerCase();
    const name = (a.lead?.name || ((a.lead?.firstName||'')+' '+(a.lead?.lastName||'')).trim() || a.lead?.phone || 'Unknown').toLowerCase();
    return (!q||a.title?.toLowerCase().includes(q)||name.toLowerCase().includes(q))&&(!sf||a.status===sf);
  });
  const tp=Math.ceil(filtered.length/PS)||1;
  const rows=filtered.slice((page-1)*PS,page*PS);

  const fmt = dt => dt?toLocalInputValue(dt):'';

  const openAdd = () => {
    const now=new Date();now.setMinutes(0,0,0);
    const end=new Date(now);end.setHours(end.getHours()+1);
    setEditA(null);
    setForm({...EF,startTime:toLocalInputValue(now),endTime:toLocalInputValue(end)});
    setFe('');setOpen(true);
  };

  const openEdit = a => {
    setEditA(a);
    setForm({title:a.title||'',lead:a.lead?._id||a.lead||'',assignedTo:a.user?._id||a.user||'',startTime:fmt(a.startTime),endTime:fmt(a.endTime),status:a.status||'scheduled',notes:a.notes||'',location:a.location||'',requirement:a.requirement||'',message:a.message||'',appointmentStatus:a.appointmentStatus||'pending'});
    setFe('');setOpen(true);
  };

  const save = async () => {
    if(!form.title||!form.lead||!form.startTime||!form.endTime) return setFe('Title, lead, start and end time required');
    if(new Date(form.endTime)<=new Date(form.startTime)) return setFe('End time must be after start time');
    try {
      setSaving(true);setFe('');
      const payload = {...form, user: form.assignedTo || undefined};
      if(editA) await axios.put(API+'/appointments/'+editA._id,payload,hdr);
      else await axios.post(API+'/appointments',payload,hdr);
      setOpen(false);load();
    } catch(e) { setFe(e.response?.data?.message||'Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    const remarks = window.prompt('Reason for deleting? (Lead will return to Pre-Leads as On Hold)','');
    if(remarks === null) return;
    try {
      await axios.delete(API+'/appointments/'+id+'?remarks='+encodeURIComponent(remarks||'Deleted'),hdr);
      setSuccess('Appointment deleted — lead returned to Pre-Leads queue');
      setTimeout(()=>setSuccess(''),4000);
      load();
    } catch(e) { setError(e.response?.data?.message||'Failed to delete'); }
  };

  const updateStatus = async (a,status) => {
    try {
      if(status==='cancelled') {
        const reason = window.prompt('Reason for cancellation? (Lead will return to Pre-Leads as On Hold)','');
        if(reason===null) return;
        await axios.put(API+'/appointments/'+a._id+'/cancel',{reason:reason||'Cancelled'},hdr);
        setSuccess('Appointment cancelled — lead returned to Pre-Leads queue');
      } else if(status==='completed') {
        const remarks = window.prompt('Completion remarks? (Lead will be marked as Converted)','');
        if(remarks===null) return;
        await axios.put(API+'/appointments/'+a._id+'/confirm',{remarks:remarks||'Completed'},hdr);
        setSuccess('Appointment completed — lead marked as Converted');
      } else {
        await axios.put(API+'/appointments/'+a._id,{status},hdr);
      }
      setTimeout(()=>setSuccess(''),4000);
      load();
    } catch(e) { setError(e.response?.data?.message||'Failed to update status'); }
  };

  const openVisit = (a) => {
    setVisitAppt(a);
    setVisitForm({visitCompleted:true,visitOutcome:a.visitOutcome||'',visitRemarks:a.visitRemarks||'',visitDate:a.visitDate?toLocalInputValue(a.visitDate):toLocalInputValue(new Date())});
    setVisitError('');setVisitOpen(true);
  };

  const saveVisit = async () => {
    if(!visitForm.visitOutcome) return setVisitError('Please select a visit outcome');
    try {
      setVisitSaving(true);setVisitError('');
      await axios.put(API+'/appointments/'+visitAppt._id+'/confirm', {
        remarks: visitForm.visitRemarks || visitForm.visitOutcome || 'Visit completed',
        ...visitForm,
        status: 'completed'
      }, hdr);
      setVisitOpen(false);load();
    } catch(e) { setVisitError(e.response?.data?.message||'Failed to save'); }
    finally { setVisitSaving(false); }
  };

  const openFollowUp = (a) => {
    setFollowUpAppt(a);
    setFollowUpForm({remarks:'',outcome:'',nextFollowUp:''});
    setFollowUpError('');setFollowUpOpen(true);
  };

  const saveFollowUp = async () => {
    if(!followUpForm.remarks) return setFollowUpError('Remarks are required');
    try {
      setFollowUpSaving(true);setFollowUpError('');
      await axios.post(API+'/appointments/'+followUpAppt._id+'/followup', followUpForm, hdr);
      setFollowUpOpen(false);load();
    } catch(e) { setFollowUpError(e.response?.data?.message||'Failed'); }
    finally { setFollowUpSaving(false); }
  };

  const openHistory = async (a) => {
    setHistoryAppt(a);setHistoryData([]);setHistoryOpen(true);
    try {
      const r = await axios.get(API+'/appointments/'+a._id+'/history', hdr);
      setHistoryData(r.data.history||[]);
    } catch(e) {}
  };

  const openAssign = (a) => {
    setAssignAppt(a);setAssignSP(a.assignedSalesperson?._id||'');setAssignOpen(true);
  };

  const saveAssign = async () => {
    try {
      await axios.post(API+'/appointments/'+assignAppt._id+'/assign',{salespersonId:assignSP},hdr);
      setAssignOpen(false);load();
    } catch(e) { alert(e.response?.data?.message||'Failed'); }
  };

  const openFeedback = async (a) => {
    setFbAppt(a);setFbError('');
    const lead = a.lead;
    setFbForm({...FF,
      customerName:lead?.name||((lead?.firstName||'')+' '+(lead?.lastName||'')).trim(),
      customerPhone:lead?.phone||'',customerEmail:lead?.email||''
    });
    try {
      const r = await axios.get(API+'/feedback/appointment/'+a._id,hdr);
      if(r.data.feedback) { setExistingFeedback(r.data.feedback); setFbForm({...r.data.feedback}); }
      else setExistingFeedback(null);
    } catch(e) { setExistingFeedback(null); }
    setFbOpen(true);
  };

  const saveFeedback = async () => {
    if(!fbForm.overallRating) return setFbError('Please provide an overall rating');
    try {
      setFbSaving(true); setFbError('');
      if(existingFeedback && existingFeedback._id) {
        await axios.put(API+'/feedback/'+existingFeedback._id, {...fbForm, appointment:fbAppt._id, lead:fbAppt.lead?._id||fbAppt.lead}, hdr);
      } else {
        await axios.post(API+'/feedback', {...fbForm, appointment:fbAppt._id, lead:fbAppt.lead?._id||fbAppt.lead}, hdr);
      }
      setFbSaving(false);
      setFbOpen(false);
      setSuccess('Feedback saved successfully!');
      setTimeout(()=>setSuccess(''), 3000);
    } catch(e) {
      setFbError(e.response?.data?.message||'Failed to save feedback');
      setFbSaving(false);
    }
  };

  const downloadFeedbackPDF = () => {
    const a = fbAppt;
    const lead = a?.lead;
    const name = lead?.name||((lead?.firstName||'')+' '+(lead?.lastName||'')).trim();
    const stars = (n) => n ? '★'.repeat(n)+'☆'.repeat(5-n) : 'Not rated';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Customer Feedback</title>
<style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;color:#333;padding:20px;}
h1{color:#003e6f;border-bottom:3px solid #fcb812;padding-bottom:10px;}
h2{color:#003e6f;font-size:16px;margin-top:24px;}
.logo{color:#003e6f;font-size:22px;font-weight:bold;}
.gold{color:#fcb812;}
table{width:100%;border-collapse:collapse;margin:12px 0;}
td{padding:8px 12px;border:1px solid #ddd;font-size:14px;}
td:first-child{background:#f0f4ff;font-weight:600;width:40%;}
.rating{color:#fcb812;font-size:18px;}
.yes{color:#27ae60;font-weight:bold;}
.no{color:#e74c3c;font-weight:bold;}
.footer{margin-top:40px;padding-top:10px;border-top:1px solid #ddd;font-size:11px;color:#888;text-align:center;}
@media print{body{margin:0;}.no-print{display:none;}}</style></head>
<body>
<div class="logo">⚡ ANVIL <span class="gold">ENERGY</span></div>
<p style="color:#666;font-size:12px;margin:4px 0 20px;">TeleConnect Pro — Customer Feedback Report</p>
<h1>Customer Feedback From</h1>
<h2>Appointment Details</h2>
<table>
<tr><td>Appointment Title</td><td>${a?.title||'-'}</td></tr>
<tr><td>Date & Time</td><td>${a?.startTime?new Date(a.startTime).toLocaleString('en-IN',{dateStyle:'full',timeStyle:'short'}):'-'}</td></tr>
<tr><td>Location</td><td>${a?.location||'-'}</td></tr>
</table>
<h2>Customer Details</h2>
<table>
<tr><td>Name</td><td>${fbForm.customerName||name||'-'}</td></tr>
<tr><td>Phone</td><td>${fbForm.customerPhone||lead?.phone||'-'}</td></tr>
<tr><td>Email</td><td>${fbForm.customerEmail||lead?.email||'-'}</td></tr>
</table>
<h2>Ratings</h2>
<table>
<tr><td>Overall Experience</td><td class="rating">${stars(fbForm.overallRating)} (${fbForm.overallRating||0}/5)</td></tr>
<tr><td>Service Quality</td><td class="rating">${stars(fbForm.serviceRating)} (${fbForm.serviceRating||0}/5)</td></tr>
<tr><td>Staff Behaviour</td><td class="rating">${stars(fbForm.staffBehavior)} (${fbForm.staffBehavior||0}/5)</td></tr>
<tr><td>Product Knowledge</td><td class="rating">${stars(fbForm.productKnowledge)} (${fbForm.productKnowledge||0}/5)</td></tr>
</table>
<h2>Additional Information</h2>
<table>
<tr><td>Would Recommend Anvil Energy?</td><td class="${fbForm.wouldRecommend?'yes':'no'}">${fbForm.wouldRecommend?'Yes ✓':'No ✗'}</td></tr>
<tr><td>Interested in Solar Installation?</td><td class="${fbForm.interestedInSolar?'yes':'no'}">${fbForm.interestedInSolar?'Yes ✓':'No ✗'}</td></tr>
<tr><td>Follow-up Required?</td><td class="${fbForm.followUpRequired?'yes':'no'}">${fbForm.followUpRequired?'Yes ✓':'No ✗'}</td></tr>
</table>
<h2>Customer Comments</h2>
<table><tr><td colspan="2" style="font-style:italic;">${fbForm.comments||'No comments provided.'}</td></tr></table>
<div class="footer">Generated by TeleConnect Pro — Anvil Energy Pvt. Ltd — ${new Date().toLocaleDateString('en-IN',{dateStyle:'full'})}</div>
<script>window.onload=()=>{window.print();}<\/script></body></html>`;
    const win = window.open('','_blank');
    win.document.write(html);
    win.document.close();
  };

  const leadName = (l) => { if(!l) return 'Unknown'; return (l.name || ((l.firstName||'')+' '+(l.lastName||'')).trim() || l.phone || 'Unknown'); };

  if(loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress sx={{color:'#003e6f'}}/></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box><Typography variant="h5" fontWeight={700}>Appointments</Typography>
        <Typography variant="body2" color="text.secondary">{filtered.length} appointments · Page {page} of {tp}</Typography></Box>
        {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'lead_management') && <Button variant="contained" startIcon={<Add/>} onClick={openAdd} sx={{bgcolor:'#003e6f','&:hover':{bgcolor:'#002a4d'},borderRadius:2}}>Add Appointment</Button>}
      </Box>

      {error&&<Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {success&&<Alert severity="success" sx={{mb:2}} onClose={()=>setSuccess('')}>{success}</Alert>}

      <Card sx={{mb:2}}><CardContent sx={{py:1.5,'&:last-child':{pb:1.5}}}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search title or lead..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
            InputProps={{startAdornment:<InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}} sx={{minWidth:260}}/>
          <FormControl size="small" sx={{minWidth:140}}><InputLabel>Status</InputLabel>
            <Select value={sf} label="Status" onChange={e=>{setSf(e.target.value);setPage(1);}}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(SC).map(s=><MenuItem key={s} value={s} sx={{color:SC[s],fontWeight:600}}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
          {(search||sf)&&<Button size="small" onClick={()=>{setSearch('');setSf('');setPage(1);}}>Clear</Button>}
        </Box>
      </CardContent></Card>

      <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
        <Table size="small">
          <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:600,fontSize:12,color:'#555',py:1.5}}}>
            <TableCell>#</TableCell><TableCell>Title</TableCell><TableCell>Lead</TableCell>
            <TableCell>Start</TableCell><TableCell>End</TableCell><TableCell>Location</TableCell>
            <TableCell>Status</TableCell><TableCell>Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.length===0
              ?<TableRow><TableCell colSpan={8} align="center" sx={{py:5,color:'text.secondary'}}>No appointments found</TableCell></TableRow>
              :rows.map((a,i)=>(
              <TableRow key={a._id} hover sx={{'&:hover':{bgcolor:'#F0F4FF'}}}>
                <TableCell sx={{color:'#999',fontSize:11}}>{(page-1)*PS+i+1}</TableCell>
                <TableCell><Typography variant="body2" fontWeight={500}>{a.title}</Typography></TableCell>
                <TableCell><Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{width:26,height:26,fontSize:11,bgcolor:'#003e6f15',color:'#003e6f',fontWeight:700}}>{leadName(a.lead).charAt(0)}</Avatar>
                  <Typography variant="body2" fontWeight={500}>{leadName(a.lead)}{!a.lead && <span style={{color:'#999',fontSize:11}}> (no lead)</span>}</Typography>
                </Box></TableCell>
                <TableCell sx={{fontSize:12}}>{new Date(a.startTime).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})}</TableCell>
                <TableCell sx={{fontSize:12}}>{new Date(a.endTime).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})}</TableCell>
                <TableCell sx={{fontSize:12}}>{a.location||'-'}</TableCell>
                <TableCell><Chip label={a.status} size="small" sx={{bgcolor:SC[a.status]+'18',color:SC[a.status],fontWeight:600,fontSize:10,height:20}}/></TableCell>
                <TableCell><Box display="flex" gap={0.5} flexWrap="wrap">
                  {a.status==='scheduled'&&user?.role!=='telecaller'&&<Tooltip title="Confirm"><IconButton size="small" onClick={()=>updateStatus(a,'confirmed')} sx={{color:'#9C27B0'}}><CheckCircle sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {a.status==='confirmed'&&<Tooltip title="Complete"><IconButton size="small" onClick={()=>updateStatus(a,'completed')} sx={{color:'#4CAF50'}}><CheckCircle sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {['scheduled','confirmed'].includes(a.status)&&<Tooltip title="Cancel"><IconButton size="small" onClick={()=>updateStatus(a,'cancelled')} sx={{color:'#F44336'}}><Cancel sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {a.status==='completed' && <Tooltip title="Visit Outcome"><IconButton size="small" onClick={()=>openVisit(a)} sx={{color:'#4CAF50'}}><AssignmentTurnedIn sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {user?.role!=='telecaller'&&<Tooltip title="Add Follow-up"><IconButton size="small" onClick={()=>openFollowUp(a)} sx={{color:'#2196F3'}}><History sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {user?.role!=='telecaller'&&<Tooltip title="Follow-up History"><IconButton size="small" onClick={()=>openHistory(a)} sx={{color:'#9C27B0'}}><Timeline sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {(user?.role==='admin'||user?.role==='lead_management') && <Tooltip title="Assign to Salesperson"><IconButton size="small" onClick={()=>openAssign(a)} sx={{color:'#FF9800'}}><PersonAdd sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {user?.role!=='telecaller'&&<Tooltip title="Solar RGF Form"><IconButton size="small" onClick={()=>navigate('/solar-rgf?appointment='+a._id)} sx={{color:'#E8622A'}}><Assignment sx={{fontSize:15}}/></IconButton></Tooltip>}
                  {(user?.role==='salesperson'||user?.role==='admin'||user?.role==='superadmin') && <Tooltip title="Feedback From"><IconButton size="small" onClick={()=>openFeedback(a)} sx={{color:'#FF9800'}}><Feedback sx={{fontSize:15}}/></IconButton></Tooltip>}
                  <Tooltip title="Edit"><IconButton size="small" onClick={()=>openEdit(a)} sx={{color:'#2196F3'}}><Edit sx={{fontSize:15}}/></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={()=>del(a._id)} sx={{color:'#F44336'}}><Delete sx={{fontSize:15}}/></IconButton></Tooltip>
                </Box></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" color="text.secondary">Showing {rows.length>0?(page-1)*PS+1:0}–{Math.min(page*PS,filtered.length)} of {filtered.length}</Typography>
        <Box display="flex" gap={0.5}>
          <Button size="small" variant="outlined" disabled={page===1} onClick={()=>setPage(p=>p-1)} startIcon={<PrevIcon/>} sx={{minWidth:80,borderColor:'#003e6f',color:'#003e6f','&:disabled':{borderColor:'#ddd',color:'#ddd'}}}>Prev</Button>
          {Array.from({length:Math.min(tp,5)},(_,i)=>i+1).map(p=>(
            <Button key={p} size="small" onClick={()=>setPage(p)} sx={{minWidth:32,height:32,p:0,borderRadius:1,bgcolor:page===p?'#003e6f':'transparent',color:page===p?'white':'#666'}}>{p}</Button>
          ))}
          <Button size="small" variant="outlined" disabled={page===tp} onClick={()=>setPage(p=>p+1)} endIcon={<NextIcon/>} sx={{minWidth:80,borderColor:'#003e6f',color:'#003e6f','&:disabled':{borderColor:'#ddd',color:'#ddd'}}}>Next</Button>
        </Box>
      </Box>

      {/* Add/Edit Appointment Dialog */}
      <Dialog open={open} onClose={()=>setOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700}>{editA?'Edit Appointment':'Add Appointment'}</DialogTitle>
        <DialogContent>
          {fe&&<Alert severity="error" sx={{mb:2,mt:1}}>{fe}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth label="Title *" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} size="small"/>
            <FormControl fullWidth size="small"><InputLabel>Lead *</InputLabel>
              <Select value={form.lead} label="Lead *" onChange={e=>setForm({...form,lead:e.target.value})}>
                {leads.map(l=><MenuItem key={l._id} value={l._id}>{l.name||((l.firstName||'')+' '+(l.lastName||'')).trim()} — {l.phone}</MenuItem>)}
              </Select>
            </FormControl>
            {(user?.role==='admin'||user?.role==='salesperson') && (
              <FormControl fullWidth size="small"><InputLabel>Assign To (Telecaller)</InputLabel>
                <Select value={form.assignedTo} label="Assign To (Telecaller)" onChange={e=>setForm({...form,assignedTo:e.target.value})}>
                  <MenuItem value="">Self</MenuItem>
                  {users.filter(u=>u.role==='salesperson').map(u=><MenuItem key={u._id} value={u._id}>{u.name||u.email}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <Box display="flex" gap={2}>
              <TextField fullWidth label="Start Time *" type="datetime-local" value={form.startTime} onChange={e=>setForm({...form,startTime:e.target.value})} size="small" InputLabelProps={{shrink:true}}/>
              <TextField fullWidth label="End Time *" type="datetime-local" value={form.endTime} onChange={e=>setForm({...form,endTime:e.target.value})} size="small" InputLabelProps={{shrink:true}}/>
            </Box>
            <Box display="flex" gap={2}>
              <TextField fullWidth label="Location" value={form.location} onChange={e=>setForm({...form,location:e.target.value})} size="small"/>
              <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={e=>setForm({...form,status:e.target.value})}>
                  {Object.keys(SC).map(s=><MenuItem key={s} value={s} sx={{color:SC[s],fontWeight:600}}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField fullWidth label="Notes" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} size="small" multiline rows={2}/>
            <TextField fullWidth label="Customer Requirement" value={form.requirement||''} onChange={e=>setForm({...form,requirement:e.target.value})} size="small" multiline rows={2} placeholder="Solar requirement, system size, budget..."/>
            <TextField fullWidth label="Message / Remarks" value={form.message||''} onChange={e=>setForm({...form,message:e.target.value})} size="small" multiline rows={2} placeholder="Initial message from customer..."/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{bgcolor:'#003e6f','&:hover':{bgcolor:'#002a4d'},borderRadius:2,px:3}}>
            {saving?<CircularProgress size={18} sx={{color:'white'}}/>:(editA?'Update':'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={fbOpen} onClose={()=>setFbOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} sx={{display:'flex',alignItems:'center',gap:1}}>
          <Feedback sx={{color:'#FF9800'}}/> Customer Feedback
          {fbAppt&&<Typography variant="caption" sx={{ml:1,color:'text.secondary'}}>— {fbAppt.title}</Typography>}
        </DialogTitle>
        <DialogContent>
          {fbError&&<Alert severity="error" sx={{mb:2,mt:1}}>{fbError}</Alert>}
          {existingFeedback&&<Alert severity="info" sx={{mb:2,mt:1}}>Feedback already submitted. You can update it below.</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">Customer Details</Typography>
            <Box display="flex" gap={2}>
              <TextField fullWidth label="Customer Name" value={fbForm.customerName} onChange={e=>setFbForm({...fbForm,customerName:e.target.value})} size="small"/>
              <TextField fullWidth label="Phone" value={fbForm.customerPhone} onChange={e=>setFbForm({...fbForm,customerPhone:e.target.value})} size="small"/>
            </Box>
            <TextField fullWidth label="Email" value={fbForm.customerEmail} onChange={e=>setFbForm({...fbForm,customerEmail:e.target.value})} size="small"/>

            <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{mt:1}}>Ratings</Typography>
            {[['overallRating','Overall Experience *'],['serviceRating','Service Quality'],['staffBehavior','Staff Behaviour'],['productKnowledge','Product Knowledge']].map(([field,label])=>(
              <Box key={field} display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={14}>{label}</Typography>
                <Rating value={fbForm[field]||0} onChange={(_,v)=>setFbForm({...fbForm,[field]:v})} size="small"/>
              </Box>
            ))}

            <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{mt:1}}>Follow-up</Typography>
            <Box display="flex" flexWrap="wrap">
              <FormControlLabel control={<Checkbox checked={fbForm.wouldRecommend} onChange={e=>setFbForm({...fbForm,wouldRecommend:e.target.checked})}/>} label="Would Recommend Anvil Energy"/>
              <FormControlLabel control={<Checkbox checked={fbForm.interestedInSolar} onChange={e=>setFbForm({...fbForm,interestedInSolar:e.target.checked})}/>} label="Interested in Solar"/>
              <FormControlLabel control={<Checkbox checked={fbForm.followUpRequired} onChange={e=>setFbForm({...fbForm,followUpRequired:e.target.checked})}/>} label="Follow-up Required"/>
            </Box>
            <TextField fullWidth label="Customer Comments" value={fbForm.comments} onChange={e=>setFbForm({...fbForm,comments:e.target.value})} size="small" multiline rows={3} placeholder="Enter customer feedback, remarks, or objections..."/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2,gap:1}}>
          <Button onClick={()=>setFbOpen(false)}>Cancel</Button>
          <Button variant="outlined" startIcon={<Download/>} onClick={downloadFeedbackPDF} sx={{borderColor:'#003e6f',color:'#003e6f'}}>Download PDF</Button>
          <Button variant="contained" onClick={saveFeedback} disabled={fbSaving} sx={{bgcolor:'#FF9800','&:hover':{bgcolor:'#e65100'},borderRadius:2,px:3}}>
            {fbSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visit Outcome Dialog */}
      <Dialog open={visitOpen} onClose={()=>setVisitOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} sx={{display:'flex',alignItems:'center',gap:1}}>
          <AssignmentTurnedIn sx={{color:'#4CAF50'}}/> Visit Outcome
          {visitAppt&&<Typography variant="caption" sx={{ml:1,color:'text.secondary'}}>— {visitAppt.title}</Typography>}
        </DialogTitle>
        <DialogContent>
          {visitError&&<Alert severity="error" sx={{mb:2,mt:1}}>{visitError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth label="Visit Date & Time" type="datetime-local" value={visitForm.visitDate}
              onChange={e=>setVisitForm({...visitForm,visitDate:e.target.value})} size="small" InputLabelProps={{shrink:true}}/>
            <FormControl fullWidth size="small">
              <InputLabel>Visit Outcome *</InputLabel>
              <Select value={visitForm.visitOutcome} label="Visit Outcome *" onChange={e=>setVisitForm({...visitForm,visitOutcome:e.target.value})}>
                <MenuItem value="quotation_sent">Quotation Sent</MenuItem>
                <MenuItem value="revisit_required">Revisit Required</MenuItem>
                <MenuItem value="closed_won">Closed / Won</MenuItem>
                <MenuItem value="dropped">Dropped</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Visit Remarks" value={visitForm.visitRemarks}
              onChange={e=>setVisitForm({...visitForm,visitRemarks:e.target.value})}
              size="small" multiline rows={3} placeholder="What happened during the visit? Customer response, next steps..."/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setVisitOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveVisit} disabled={visitSaving}
            sx={{bgcolor:'#4CAF50','&:hover':{bgcolor:'#388E3C'},borderRadius:2,px:3}}>
            {visitSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Visit Outcome'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={followUpOpen} onClose={()=>setFollowUpOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} sx={{display:'flex',alignItems:'center',gap:1}}>
          <History sx={{color:'#2196F3'}}/> Add Follow-up
          {followUpAppt&&<Typography variant="caption" sx={{ml:1,color:'text.secondary'}}>— {followUpAppt.title}</Typography>}
        </DialogTitle>
        <DialogContent>
          {followUpError&&<Alert severity="error" sx={{mb:2,mt:1}}>{followUpError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth label="Remarks *" value={followUpForm.remarks} onChange={e=>setFollowUpForm({...followUpForm,remarks:e.target.value})} size="small" multiline rows={3} placeholder="What happened? Customer response..."/>
            <FormControl fullWidth size="small"><InputLabel>Outcome</InputLabel>
              <Select value={followUpForm.outcome} label="Outcome" onChange={e=>setFollowUpForm({...followUpForm,outcome:e.target.value})}>
                <MenuItem value="">In Progress</MenuItem>
                <MenuItem value="follow_up">Needs Follow-up</MenuItem>
                <MenuItem value="converted">Converted ✓</MenuItem>
                <MenuItem value="closed">Closed ✗</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Next Follow-up Date" type="datetime-local" value={followUpForm.nextFollowUp} onChange={e=>setFollowUpForm({...followUpForm,nextFollowUp:e.target.value})} size="small" InputLabelProps={{shrink:true}}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setFollowUpOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveFollowUp} disabled={followUpSaving} sx={{bgcolor:'#2196F3','&:hover':{bgcolor:'#1565C0'},borderRadius:2,px:3}}>
            {followUpSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Follow-up'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={()=>setHistoryOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} sx={{display:'flex',alignItems:'center',gap:1}}>
          <Timeline sx={{color:'#9C27B0'}}/> Follow-up History
          {historyAppt&&<Typography variant="caption" sx={{ml:1,color:'text.secondary'}}>— {historyAppt.title}</Typography>}
        </DialogTitle>
        <DialogContent>
          {historyData.length===0
            ?<Box sx={{textAlign:'center',py:4,color:'text.secondary'}}><Timeline sx={{fontSize:40,opacity:0.3}}/><Typography fontSize={13} mt={1}>No follow-up history yet</Typography></Box>
            :historyData.map((h,i)=>(
              <Box key={i} sx={{p:1.5,border:'1px solid #e0e7ff',borderRadius:1.5,mb:1.5,position:'relative'}}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography fontSize={12} fontWeight={700} color="primary">{h.addedBy?.name||'Unknown'}</Typography>
                  <Typography fontSize={11} color="text.secondary">{h.date?new Date(h.date).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'}):''}</Typography>
                </Box>
                <Typography fontSize={13}>{h.remarks}</Typography>
                {h.outcome&&<Chip label={h.outcome.replace(/_/g,' ')} size="small" sx={{mt:0.5,fontSize:10,height:18,bgcolor:h.outcome==='converted'?'#4CAF5018':h.outcome==='closed'?'#F4433618':'#2196F318',color:h.outcome==='converted'?'#4CAF50':h.outcome==='closed'?'#F44336':'#2196F3'}}/>}
                {h.nextFollowUp&&<Typography fontSize={11} color="text.secondary" mt={0.3}>Next: {new Date(h.nextFollowUp).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})}</Typography>}
              </Box>
            ))
          }
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Salesperson Dialog */}
      <Dialog open={assignOpen} onClose={()=>setAssignOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} sx={{display:'flex',alignItems:'center',gap:1}}>
          <PersonAdd sx={{color:'#FF9800'}}/> Assign Appointment
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <FormControl fullWidth size="small"><InputLabel>Select Salesperson</InputLabel>
              <Select value={assignSP} label="Select Salesperson" onChange={e=>setAssignSP(e.target.value)}>
                {users.filter(u=>u.role==='salesperson').map(u=><MenuItem key={u._id} value={u._id}>{u.name||u.email}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssign} disabled={!assignSP} sx={{bgcolor:'#FF9800','&:hover':{bgcolor:'#F57C00'},borderRadius:2,px:3}}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
